/**
 * @module cercaliagl/control/Custom
 */
import Control from './Control.js';
import { createUid } from '../util.js';
import VControlButton from '../view/VControlButton.js';

/**
 * @typedef {Object} CustomOptions
 * @property {string|undefined} svgPath  String with SVG path. `https://developer.mozilla.org/en-US/docs/Web/SVG/Element/path`.
 * Programs as Adobe Illustrator can generate icons done with SVG.<br/>
 * Default: `M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z`.<br/>
 * If you need help, you can send us which icon you want to convert to SVG.
 * @property {Function|undefined} clickFunction Function to execute on click the control.
 * @property {string|undefined} name Control name. You can get this control throught `getControlByName` method from `cercalia.Map` class.
 * @property {string|undefined} title Tooltip. Default '' (empty).
 * @property {boolean|undefined} twoState Two state button if `true`. Default `false`. In callback function you can get its state using `isEnabled()` function.
 */

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.Custom({ ... }); // see below
 * ```
 * <br/>
 * Provides a custom control to create an additional button in buttons bar. Use provides a SVG path to draw an icon. This icon should be respect the style and
 * it will adopt the theme color of map.<br/>
 * <br/>
 * The control can have one or two states providing `twoState` option.<br/>
 * A callback function `clickFunction` is called when button is clicked and the state can be accessed throught {@link module:cercaliagl/control/Custom~Custom#isEnabled} method.<br/>
 * <br/>
 *
 * ```js
 * const map = new cercaliagl.Map({
 *   target: 'map',
 *   controls: [],
 * });
 * map.whenReady(() => {
 *   const customControl = new cercaliagl.control.Custom({
 *     svgPath: 'M16.84,2.73C16.45,2.73 16.07,2.88 15.77,3.17L13.65,5.29L18.95,10.6L21.07,8.5C21.67,7.89 21.67,6.94 21.07,6.36L17.9,3.17C17.6,2.88 17.22,2.73 16.84,2.73M12.94,6L4.84,14.11L7.4,14.39L7.58,16.68L9.86,16.85L10.15,19.41L18.25,11.3M4.25,15.04L2.5,21.73L9.2,19.94L8.96,17.78L6.65,17.61L6.47,15.29',
 *     title: 'Custom control tittle',
 *     twoState: true,
 *     clickFunction: () => {
 *       const lon = 0 - Math.random();
 *       const lat = 41 - Math.random();
 *       const position = new cercaliagl.LonLat(lon, lat);
 *       const marker = new cercaliagl.Marker({ position });
 *       map.addMarker(marker);
 *       map.setCenter(position, 12);
 *     }
 *   map.addCustomControl(customControl, 0);
 * });
 * ```
 *
 * @constructor
 * @extends {Control}
 * @param {CustomOptions} options Options.
 * @api
 */
class Custom extends Control {
  constructor(options) {
    const controlButton = new VControlButton('');
    const element = controlButton.getElement();

    super({
      element,
      className: 'cercaliagl.control.Custom',
      name: options.name ? options.name : `CustomControl${createUid()}`,
    });

    /**
     * @private
     * @type {VControlButton}
     */
    this.controlButton_ = controlButton;

    /**
     * @private
     * @type {string}
     */
    this.defaultSvgPath_ = 'M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z';

    /**
     * @private
     * @type {HTMLElement}
     */
    this.element_ = element;

    /**
     * @private
     * @type {string}
     */
    this.svgPath_ = options.svgPath ? options.svgPath : this.defaultSvgPath_;

    /**
     * @private
     * @type {string|undefined}
     */
    this.title_ = options.title ? options.title : undefined;

    /**
     * @private
     * @type {boolean}
     */
    this.twoState_ = options.twoState !== undefined ? options.twoState : false;

    /**
     * @private
     * @type {Function}
     */
    this.clickFn_ = options.clickFunction;

    this.initialize_();
  }

  /**
   * @private
   */
  initialize_() {
    if (this.title_) {
      this.controlButton_.setTooltip(this.title_);
    }
    this.controlButton_.setSvgPath(this.svgPath_);
    this.element_.addEventListener('click', this.handleClick_.bind(this));
  }

  /**
   * @private
   */
  handleClick_() {
    if (!this.controlButton_.isEnabled()) {
      this.controlButton_.enable(this.twoState_);
    } else {
      this.controlButton_.disable();
    }
    setTimeout(() => {
      this.clickFn_.call(this, this.controlButton_.isEnabled());
    }, 10);
  }

  /**
   * Return if control is enabled. Only works when `twoState` option is `true`.
   * @api
   * @return {boolean} Return `true` if button is enabled.
   */
  isEnabled() {
    if (this.twoState_) {
      return this.controlButton_.isEnabled();
    }
    return false;
  }

  /**
   * Enable control
   * @api
   * @override
   */
  enable() {
    this.controlButton_.enable(this.twoState_);
    this.clickFn_.call(this, true);
  }

  /**
   * Disable control
   * @api
   * @override
   */
  disable() {
    this.controlButton_.disable();
    this.clickFn_.call(this, false);
  }
}

export default Custom;
